import React, {useEffect, useMemo, useState} from 'react'
import {Box, Spinner, Text} from 'grommet'
import {InputNumber, Modal} from 'antd'
import {NetworkSelect} from "./NetworkSelect";
import {TokenSelect} from "./TokenSelect";
import {GradientFilledButton} from "../../components/button";
import {harmonyOne} from "wagmi/chains";
import {BridgeNetworkMap} from "../../constants";
import {
  BridgeOperationType,
  BridgeToken,
  CreateBridgeOperationParams,
  createOperation
} from "../../api/bridge";
import {useAccount, useBalance, useNetwork} from "wagmi";
import { v4 as uuidv4 } from 'uuid';
import {useNavigate, useParams} from "react-router-dom";
import {OperationTrack} from "./OperationTrack";
import {useBridge} from "../../providers/BridgeDataProvider";
import {arbitrum, bsc, mainnet, base, linea} from "viem/chains";
import {parseUnits, formatUnits} from "viem";
import {switchNetwork} from "@wagmi/core";
import {useFeeEstimate} from "../../hooks/useFeeEstimate";

export interface BridgeState {
  from: {
    networkIds: number[]
    networkId?: number
    token?: BridgeToken
    amount: string
  }
  to: {
    networkIds: number[]
    networkId?: number
    token?: BridgeToken
    amount: string
  }
}

const DefaultBridgeState: BridgeState = {
  from: {
    networkIds: [bsc.id, arbitrum.id],
    networkId: bsc.id,
    amount: ''
  },
  to: {
    networkIds: [harmonyOne.id],
    networkId: harmonyOne.id,
    amount: ''
  }
}

export type ValueType = string | number | undefined | null

const formatterHelper = (value: ValueType) => {
  const valueStr = (value || '').toString()
  const [decimalPart, fractionalPart = ''] = valueStr.split('.')
  const decimalFormatted = `${decimalPart}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const fractionalFormatted = fractionalPart ? `.${fractionalPart}` : ''
  return decimalFormatted + fractionalFormatted
}
const parserHelper = (value: ValueType) => `${value}`.replace(/\$\s?|(,*)/g, '')

export const Bridge = () => {
  const { address: userAddress } = useAccount()
  const { chain } = useNetwork()
  const navigate = useNavigate()
  const routeParams = useParams()

  const [bridgeState, setBridgeState] = useState<BridgeState>(DefaultBridgeState)
  const { isLoading, tokens } = useBridge()
  const { data: bridgeFee } = useFeeEstimate({ userAddress, erc20Address: bridgeState.from.token?.erc20Address, amount: bridgeState.from.amount, chainId: bridgeState.from.networkId })

  const { data: erc20BalanceData } = useBalance({
    address: userAddress,
    token: bridgeState.from.token?.erc20Address as `0x${string}`,
    enabled: !!bridgeState.from.token?.erc20Address,
    chainId: bridgeState.from.networkId,
    watch: !!bridgeState.from.token,
  })

  const tokensListFrom = useMemo(() => {
    return tokens.filter(item => bridgeState.from.networkId
      ? item.network === BridgeNetworkMap[bridgeState.from.networkId]
        && ['USDT', 'ONE'].includes(item.symbol)
      : false)
  }, [tokens, bridgeState.from.networkId])

  useEffect(() => {
    // Initial setup
    if(!bridgeState.from.token && tokens.length > 0) {
      setBridgeState((state) => ({
        ...state,
        from: {
          ...state.from,
          token: tokensListFrom[0]
        }
      }))
    }
  }, [tokens, tokensListFrom, bridgeState]);

  const setAmount = (value: string) => {
    setBridgeState((state) => ({
      ...state,
      from: {
        ...state.from,
        amount: value
      }
    }))
  }

  const onConfirmClicked = async () => {
    try {
      if(!bridgeState.from.token) {
        throw new Error('No "from" token defined')
      }
      const amount = bridgeState.from.amount
      const params: CreateBridgeOperationParams = {
        oneAddress: userAddress as string,
        ethAddress: userAddress as string,
        amount,
        approveAmount: amount,
        erc20Address: bridgeState.from.token.erc20Address,
        hrc20Address: bridgeState.from.token.hrc20Address,
        "hrc721Address":"",
        "nftName":"",
        "nftImageUrl":"",
        "ensName":"",
        type: BridgeOperationType.ethToOne,
        token: bridgeState.from.token.type,
        network: bridgeState.from.token.network,
        id: uuidv4()
      }
      console.log('Creating new operation...', params)
      const operation = await createOperation(params)
      console.log('Operation created:', operation)
      navigate(`/bridge/operation/${params.id}`)
    } catch (e) {
      console.error('Failed to bridge', e)
    }
  }

  const amount = bridgeState.from.token ? parseUnits(bridgeState.from.amount, +bridgeState.from.token?.decimals) : 0n
  const isSufficientBalance = amount > 0 && erc20BalanceData && erc20BalanceData.value >= amount
  const isNetworkSwitched = chain?.id === bridgeState.from.networkId
  const isDisabled = !isSufficientBalance || !isNetworkSwitched
  const commonButtonProps = {
    style: {
      height: '48px',
    }
  }
  const confirmButtonProps = {
    ...commonButtonProps,
    disabled: isDisabled,
    style: {
      ...commonButtonProps.style,
      opacity: isDisabled ? 0.4 : 1,
    }
  }

  const sendPriceFormatted = useMemo(() => {
    let amountUsd = 0
    if(tokens.length > 0 && bridgeState.from.amount && bridgeState.from.token) {
      const decimals = +bridgeState.from.token.decimals
      const amount = parseUnits(bridgeState.from.amount, decimals)
      const tokenValue = formatUnits(amount, decimals)
      amountUsd = +tokenValue * +bridgeState.from.token.usdPrice
      // return (+tokenValue * +bridgeState.from.token.usdPrice).toFixed(2).toLocaleUpperCase()
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amountUsd)
  }, [tokens, bridgeState.from.token?.symbol, bridgeState.from.amount])

  return <Box pad={'52px 32px'} width={'100%'}>
    <Box>
      <Text size={'42px'}>Bridge</Text>
    </Box>
    <Box margin={{ top: '60px' }} align={'center'}>
      <Box
        background={'#323232'}
        pad={'12px 24px 32px'}
        round={'28px'}
        width={'700px'}
      >
        <Box margin={{ top: '16px' }} gap={'8px'}>
          <Text weight={500} size={'16px'}>Bridge tokens</Text>
          <Text>Transfer your tokens from one network to another.</Text>
        </Box>
        <Box margin={{ top: '32px' }} direction={'row'} justify={'between'} gap={'32px'}>
          <Box gap={'4px'} width={'50%'}>
            <Text weight={500} size={'16px'}>From this network</Text>
            <NetworkSelect
              value={bridgeState.from.networkId ? [bridgeState.from.networkId] : undefined}
              networksIds={bridgeState.from.networkIds}
              onChange={(networkId) => {
                const networkTokens = tokens.filter(item => +item.network === networkId)

                setBridgeState((currentState) => {
                  return {
                    ...currentState, from: { ...currentState.from, networkId, token: networkTokens[0], amount: '0' }
                  }
                })
              }}
            />
          </Box>
          <Box gap={'4px'} width={'50%'}>
            <Text weight={500} size={'16px'}>To this network</Text>
            <NetworkSelect
              value={bridgeState.to.networkId ? [bridgeState.to.networkId] : undefined}
              networksIds={bridgeState.to.networkIds}
              onChange={(networkId) => {
                setBridgeState((currentState) => {
                  return {
                    ...currentState, to: { ...currentState.to, networkId }
                  }
                })
              }}
            />
          </Box>
        </Box>
        {/*<Box align={'center'} justify={'center'} margin={{ top: '8px' }} onClick={() => {*/}
        {/*  setBridgeState((currentState) => {*/}
        {/*    return {*/}
        {/*      ...currentState, to: { ...currentState.from }, from: { ...currentState.to }*/}
        {/*    }*/}
        {/*  })*/}
        {/*}}>*/}
        {/*  <Unsorted size={'48px'} style={{ transform: 'rotate(90deg)' }} />*/}
        {/*</Box>*/}
        <Box direction={'row'} justify={'between'} gap={'32px'} margin={{ top: '32px' }}>
          <Box gap={'4px'} width={'50%'}>
            <Text weight={500} size={'16px'}>You send</Text>
            <TokenSelect
              tokens={tokensListFrom}
              token={bridgeState.from.token}
              onChange={(token) => {
                setBridgeState((currentState) => {
                  return {
                    ...currentState, from: { ...currentState.from, token }
                  }
                })
              }}
            />
          </Box>
          <Box gap={'4px'} width={'50%'}>
            <Text weight={500} size={'16px'}>Amount</Text>
            <InputNumber
              placeholder={'0.00'}
              size={'large'}
              value={bridgeState.from.amount}
              min={0}
              max={100000000}
              formatter={formatterHelper}
              parser={parserHelper}
              onChange={(value) => {
                setAmount((value || 0).toString())
              }}
              style={{ width: '100%' }}
            />
            <Box align={'end'}>
              <Box direction={'row'} gap={'6px'}>
                <Text>Balance: {erc20BalanceData?.formatted || '0'} {bridgeState.from.token?.symbol || ''}</Text>
                {erc20BalanceData && erc20BalanceData.value > 0n &&
                    <Box onClick={() => {
                      setAmount(erc20BalanceData.formatted)
                    }}><Text weight={500}>Max</Text></Box>
                }
              </Box>
            </Box>
          </Box>
        </Box>
        <Box direction={'row'} margin={{ top: '48px' }} justify={'between'}>
          <Box width={'45%'}>
            <Text>Total</Text>
            <Text weight={500} size={'20px'}>{sendPriceFormatted}</Text>
          </Box>
          <Box width={'100%'}>
            {!isNetworkSwitched
              ? <GradientFilledButton {...commonButtonProps} onClick={() => {
                if(bridgeState.from.networkId) {
                  switchNetwork({ chainId: bridgeState.from.networkId }).catch(e => { console.error('Failed to switch network', e) })
                }
              }}>
                  <Box direction={'row'} align={'center'} justify={'center'} gap={'8px'}>
                    <Text color={'#323232'} size={'24px'} weight={500}>
                      Switch network
                    </Text>
                  </Box>
                </GradientFilledButton>
              : <GradientFilledButton {...confirmButtonProps} onClick={onConfirmClicked}>
                  <Box direction={'row'} align={'center'} justify={'center'} gap={'8px'}>
                    {isLoading &&
                        <Spinner color={'white'} />
                    }
                    <Text color={'#323232'} size={'24px'} weight={500}>
                      Confirm
                    </Text>
                  </Box>
                </GradientFilledButton>
            }
          </Box>
        </Box>
      </Box>
    </Box>
    <Modal
      width={'600px'}
      title="Bridge Request"
      open={!!routeParams.operationId}
      okButtonProps={{ disabled: true }}
      onCancel={() => {
        navigate('/bridge')
      }}
      forceRender={true}
    >
      <OperationTrack />
    </Modal>
  </Box>
}
